import { uniqueId } from 'lodash'
import React, { FC } from 'react'
import { secToHourMinute } from '../../../utils/date'

interface Props {
  labels: {
    name: string
    color: string | string[]
  }[]
  list: {
    title: string
    values: {
      name: string
      value: number
      color: string
    }[]
  }[]
  chartSize: number
  type?: string
  gap?: boolean
  horizontal?: boolean
  displayTitle?: boolean
  ruler?: boolean
  labelSkip?: number
}

const StackedBarChart: FC<Props> = (props) => {
  const {
    labels,
    list,
    chartSize,
    type,
    gap,
    horizontal,
    displayTitle,
    ruler,
    labelSkip,
  } = props

  const buildUnit = (value: number) => {
    if (!value) return <></>
    if (type === "person") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          人
        </>
      )
    }
    if (type === "biz") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          biz
        </>
      )
    }
    if (type === "page") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          枚
        </>
      )
    }
    if (type === "time") {
      return (
        <span>
          {secToHourMinute(value)}
        </span>
      )
    }
    if (type === "times") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          回
        </>
      )
    }
    if (type === "percent") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          %
        </>
      )
    }
    return (
      <></>
    )
  }

  const buildLabel = (label: string) => {
    let fontColor = ""
    if (label === "土") {
      fontColor = "#317ab7"
    } else if (label === "日") {
      fontColor = "#ff0000"
    }
    return (
      <span
        style={{ color: fontColor }}
      >
        {label}
      </span>
    )
  }

  const buildHorizontalBarChart = () => {
    const max = Math.max(...list.map((value) => value.values.reduce((prev, val) => prev + val.value, 0))) * 1
    return list.map((value) => {
      const sum = value.values.reduce((prev, val) => prev + val.value, 0)
      let count = 0
      const bgList: string[] = []
      const labelList: {position: number, value: number, color: string}[] = []
      value.values.forEach((val) => {
        bgList.push(`${val.color} ${(count / sum) * 100}%, ${val.color} ${((count + val.value) / sum) * 100}%`)
        if (type === "percent") {
          if ((Math.round((val.value / sum) * 1000) / 10) >= 5) {
            labelList.push({
              position: (Math.round((val.value / sum) * 500) + Math.round((count / sum) * 1000)) / 10,
              value: Math.round((val.value / sum) * 1000) / 10,
              color: val.color,
            })
          }
        } else {
          labelList.push({
            position: (Math.round((val.value / sum) * 500) + Math.round((count / sum) * 1000)) / 10,
            value: val.value,
            color: val.color,
          })
        }
        count += val.value
      })

      return (
        <div
          className="row"
          style={{
            padding: gap ? "8px 0" : "0",
          }}
          key={uniqueId('bar_chart_row_')}
        >
          {
            displayTitle && (
              <div className="row_title">
                {buildLabel(value.title)}
              </div>
            )
          }
          <div
            className="row_value"
            style={{
              width: displayTitle ? "85%" : "100%",
            }}
          >
            <div
              className="stacked_bar_chart"
              style={{
                width: `${(sum / max) * 100}%`,
                background: `linear-gradient(to right, ${bgList})`,
              }}
            >
              {
                labelList.map((val) => (
                  <div
                    className="label"
                    style={{
                      color: val.color,
                      left: `${val.position}%`,
                    }}
                    key={uniqueId('stacked_bar_chart_')}
                  >
                    {buildUnit(val.value)}
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )
    })
  }

  const buildVerticalBarChart = () => {
    const max = Math.max(...list.map((value) => value.values.reduce((prev, val) => prev + val.value, 0))) * 1
    return list.map((value, index) => {
      const sum = value.values.reduce((prev, val) => prev + val.value, 0)
      let count = 0
      const bgList: string[] = []
      const labelList: {position: number, value: number, color: string}[] = []
      value.values.forEach((val) => {
        bgList.push(`${val.color} ${(count / sum) * 100}%, ${val.color} ${((count + val.value) / sum) * 100}%`)
        if (type === "percent") {
          if ((Math.round((val.value / sum) * 1000) / 10) < 10) {
            labelList.push({
              position: (Math.round((count / sum) * 1000) / 10) - 5,
              value: Math.round((val.value / sum) * 1000) / 10,
              color: val.color,
            })
          } else {
            labelList.push({
              position: (Math.round((val.value / sum) * 500) + Math.round((count / sum) * 1000)) / 10,
              value: Math.round((val.value / sum) * 1000) / 10,
              color: val.color,
            })
          }
        } else {
          labelList.push({
            position: (Math.round((val.value / sum) * 500) + Math.round((count / sum) * 1000)) / 10,
            value: val.value,
            color: val.color,
          })
        }
        count += val.value
      })

      return (
        <div
          className="column_value"
          style={{
            height: `${chartSize}px`,
            width: `calc(100% / ${list.length || 1})`,
          }}
          key={uniqueId('column_value_')}
        >
          <div
            className="stacked_bar_chart"
            style={{
              width: gap ? "80%" : "100%",
              height: `${(sum / max) * 100}%`,
              borderRight: gap ? "1px solid #FFF" : "",
              borderLeft: gap ? "1px solid #FFF" : "",
              background: `linear-gradient(to bottom, ${bgList})`,
            }}
          >
            {
              labelList.map((val) => (
                <div
                  className="label"
                  style={{
                    color: val.color,
                    top: `${val.position}%`,
                  }}
                  key={uniqueId('stacked_bar_label_')}
                >
                  {buildUnit(val.value)}
                </div>
              ))
            }
            {
              ruler ? (
                <div className="ruler_border" />
              ) : (
                <></>
              )
            }
          </div>
          {
            labelSkip && (index % labelSkip) !== 0 ? (
              <></>
            ) : (
              <div className="column_title">
                {buildLabel(value.title)}
              </div>
            )
          }
        </div>
      )
    })
  }

  return (
    <div className="stacked_bar_chart_wrap">
      {
        horizontal ? (
          <div className="horizontal">
            <div className="stacked_bar_chart_area">
              {buildHorizontalBarChart()}
            </div>
            <div className="label_area">
              {
                labels.map((value) => (
                  <div
                    className="label"
                    key={uniqueId('stacked_bar_chart_legend_')}
                  >
                    {
                      typeof value.color === 'string' ? (
                        <div
                          className="label_color"
                          style={{
                            backgroundColor: value.color,
                          }}
                        />
                      ) : (
                        <>
                          {
                            value.color.map((col) => (
                              <div
                                key={uniqueId("label_color_")}
                                className="label_color"
                                style={{
                                  backgroundColor: col,
                                }}
                              />
                            ))
                          }
                        </>
                      )
                    }
                    <div className="label_title">
                      {value.name}
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        ) : (
          <div className="vertical">
            <div
              className="stacked_bar_chart_area"
              style={{
                height: `${chartSize}px`,
                justifyContent: gap ? "space-evenly" : "center",
              }}
            >
              {buildVerticalBarChart()}
            </div>
            <div className="label_area">
              {
                labels.map((value) => (
                  <div
                    className="label"
                    key={uniqueId('stacked_bar_chart_legend_')}
                  >
                    {
                      typeof value.color === 'string' ? (
                        <div
                          className="label_color"
                          style={{
                            backgroundColor: value.color,
                          }}
                        />
                      ) : (
                        <>
                          {
                            value.color.map((col) => (
                              <div
                                key={uniqueId("label_color_")}
                                className="label_color"
                                style={{
                                  backgroundColor: col,
                                }}
                              />
                            ))
                          }
                        </>
                      )
                    }
                    <div className="label_title">
                      {value.name}
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        )
      }
    </div>
  )
}

StackedBarChart.defaultProps = {
  type: "",
  gap: true,
  horizontal: false,
  displayTitle: true,
  ruler: false,
  labelSkip: 0,
}

export default StackedBarChart
